import { useEffect, useState } from 'react';
import { expensesApi as eapi, formatCents } from '../../../api/expenses.js';

/**
 * Corporate-card reconciliation: list imported card transactions, match an
 * unmatched txn to an expense line, ignore noise, and import a batch for a card.
 */
export default function CardsPage() {
  const [txns, setTxns] = useState([]);
  const [cards, setCards] = useState([]);
  const [cardId, setCardId] = useState('');
  const [matchInputs, setMatchInputs] = useState({}); // txnId -> lineId
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([eapi.listCardTransactions(), eapi.listCards()]);
      setTxns(t?.items || []);
      const items = c?.items || [];
      setCards(items);
      if (!cardId && items.length) setCardId(items[0].id);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load card transactions');
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function run(label, fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.response?.data?.message || `${label} failed`);
    } finally {
      setBusy(false);
    }
  }

  async function match(txnId) {
    const lineId = (matchInputs[txnId] || '').trim();
    if (!lineId) { setError('Enter a line id to match'); return; }
    await run('Match', () => eapi.matchCardTransaction(txnId, { lineId }));
  }

  async function importBatch() {
    if (!cardId) { setError('Select a card to import into'); return; }
    await run('Import', () => eapi.importCardTransactions({
      cardId,
      autoMatch: true,
      transactions: [
        { postedAt: '2026-06-01', amountCents: 1200, currency: 'USD', merchant: 'Coffee Bar', externalRef: 'imp-coffee-1' },
        { postedAt: '2026-06-02', amountCents: 3400, currency: 'USD', merchant: 'Office Depot', externalRef: 'imp-office-1' },
      ],
    }));
  }

  return (
    <section data-testid="expenses-cards-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Cards</h1>
          <p className="muted">Reconcile corporate-card transactions against expense lines.</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="expenses-cards-error">{error}</div>}

      <div className="card form-card row-form">
        <label>Card
          <select value={cardId} onChange={(e) => setCardId(e.target.value)}>
            <option value="">— select —</option>
            {cards.map((c) => <option key={c.id} value={c.id}>•••• {c.last4} ({c.provider || 'card'})</option>)}
          </select>
        </label>
        <div className="actions">
          <button className="btn primary" data-testid="expenses-card-import" disabled={busy || !cardId} onClick={importBatch}>Import transactions</button>
        </div>
      </div>

      <div className="card">
        <h3>Transactions</h3>
        <table className="data-table" data-testid="expenses-card-transaction-list">
          <thead><tr><th>Posted</th><th>Merchant</th><th>Amount</th><th>Card</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.id} data-testid={`expenses-card-transaction-row-${t.id}`}>
                <td>{String(t.posted_at).slice(0, 10)}</td>
                <td>{t.merchant || '—'}</td>
                <td>{formatCents(t.amount_cents, t.currency)}</td>
                <td>•••• {t.card_last4}</td>
                <td data-testid={`expenses-card-transaction-status-${t.id}`}>{t.status}</td>
                <td>
                  {t.status === 'unmatched' && (
                    <span className="row-form">
                      <input
                        placeholder="line id"
                        value={matchInputs[t.id] || ''}
                        onChange={(e) => setMatchInputs((m) => ({ ...m, [t.id]: e.target.value }))}
                      />
                      <button className="btn primary" data-testid={`expense-card-match-${t.id}`} disabled={busy} onClick={() => match(t.id)}>Match</button>
                      <button className="btn ghost" data-testid={`expenses-card-ignore-${t.id}`} disabled={busy} onClick={() => run('Ignore', () => eapi.ignoreCardTransaction(t.id))}>Ignore</button>
                    </span>
                  )}
                  {t.status === 'matched' && <span className="muted">→ {t.matched_line_id}</span>}
                </td>
              </tr>
            ))}
            {!loading && txns.length === 0 && <tr><td colSpan={6} className="muted">No card transactions.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
